/**
 * Copyright (c) 2024 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { BrandColors, LoadingSpinner, MaterialIcon, PopoverList } from '@blend/enterprise-components';

export const styles = StyleSheet.create({
  root: {
    display: 'flex',
    alignItems: 'center',
  },
  mainButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexDirection: 'row',
    backgroundColor: BrandColors.SKY,
    color: BrandColors.WHITE,
    height: '36px',
    border: 0,
    cursor: 'pointer',
    borderRadius: '3px 0 0 3px',
    paddingInlineStart: '24px',
    paddingInlineEnd: '24px',

    ':hover:enabled': {
      backgroundColor: BrandColors.SKY_TONE_30,
    },
  },
  disabled: {
    cursor: 'not-allowed',
    pointerEvents: 'all',
    color: BrandColors.NIGHT_40,
    backgroundColor: BrandColors.CLOUD_30,
    border: 'none',
    opacity: 1,
  },
  arrowButton: {
    backgroundColor: BrandColors.SKY,
    color: BrandColors.WHITE,
    height: '36px',
    border: 0,
    cursor: 'pointer',
    borderRadius: '0 3px 3px 0',
    ':hover:enabled': {
      backgroundColor: BrandColors.SKY_TONE_30,
    },
    ':enabled': {
      borderLeft: `1px solid ${BrandColors.ACCENT_SKY_70}`,
    },
  },
});

export namespace DropdownButton {
  export interface Props {
    id: string;
    disabled?: boolean;
    isLoading?: boolean;
    label: string;
    onClick: () => void;
    extraActions?: { label: string; onClick: () => void }[];
  }
}

/** Drop down button is a button with an extension dropdown that show a popup with extra actions */
export const DropdownButton: React.FC<DropdownButton.Props> = ({
  id,
  label,
  onClick,
  extraActions,
  disabled,
  isLoading,
}) => {
  const isDisabledOrLoading = disabled || isLoading;
  const disabledStyle = isDisabledOrLoading ? styles.disabled : undefined;
  return (
    <div className={css(styles.root)} data-it={`dropdown-button-${id}`}>
      <button
        className={css(styles.mainButton, disabledStyle)}
        data-it={'button-main'}
        disabled={isDisabledOrLoading}
        id={id}
        onClick={onClick}
      >
        {isLoading && <LoadingSpinner size="md" />}
        <span>{label}</span>
      </button>

      <PopoverList btnSize={'md'} buttonConfigs={extraActions ?? []}>
        <button
          className={css(styles.arrowButton, disabledStyle)}
          data-it={`button-arrow`}
          disabled={isDisabledOrLoading}
          id={`button-arrow-${id}`}
        >
          <MaterialIcon color={BrandColors.WHITE} size={22} type={'arrow_drop_down'} />
        </button>
      </PopoverList>
    </div>
  );
};
